import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { getEffectivePermissions, hasPermission as checkPermission } from '../utils/permissions';

const AuthContext = createContext({})

/** Absolute login session length before the user must sign in again. */
export const SESSION_MAX_MINUTES = 180;
const SESSION_MAX_DURATION_MS = SESSION_MAX_MINUTES * 60 * 1000;
const SESSION_STARTED_AT_KEY = 'tbf_session_started_at';
const SESSION_CHECK_INTERVAL_MS = 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

const getSessionStartedAt = () => {
  const raw = localStorage.getItem(SESSION_STARTED_AT_KEY);
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const markSessionStarted = (timestamp = Date.now()) => {
  localStorage.setItem(SESSION_STARTED_AT_KEY, String(timestamp));
};

const clearSessionStarted = () => {
  localStorage.removeItem(SESSION_STARTED_AT_KEY);
};

const isSessionExpired = () => {
  const startedAt = getSessionStartedAt();
  if (!startedAt) return false;
  return Date.now() - startedAt >= SESSION_MAX_DURATION_MS;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const expiringRef = useRef(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        // Use maybeSingle() instead of single() to avoid 404 errors when profile doesn't exist
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle()
        
        // Handle errors gracefully - only log if it's not a "not found" error
        if (error) {
          // PGRST116 is "no rows found" - this is expected for new users without profiles
          if (error.code === 'PGRST116') {
            // User exists but no profile yet - this is okay
            setUserProfile(null)
          } else {
            // Other errors (permission, network, etc.) - log but don't break
            console.error('Profile load error:', error)
            setUserProfile(null)
          }
        } else {
          setUserProfile(data)
        }
      } catch (error) {
        // Catch any unexpected errors
        console.error('Profile load error:', error)
        setUserProfile(null)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  const expireSession = async () => {
    if (expiringRef.current) return;
    expiringRef.current = true;
    try {
      clearSessionStarted();
      localStorage.removeItem('tbf_user');
      await supabase?.auth?.signOut();
      setUser(null);
      profileOperations?.clear();

      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        const params = new URLSearchParams({ sessionExpired: '1' });
        window.location.replace(`/login?${params.toString()}`);
      }
    } finally {
      expiringRef.current = false;
    }
  };

  const enforceSessionLimit = (session) => {
    if (!session?.user) {
      clearSessionStarted();
      return false;
    }

    if (!getSessionStartedAt()) {
      // Existing persisted sessions after deploy: start the clock now.
      markSessionStarted();
    }

    if (isSessionExpired()) {
      expireSession();
      return true;
    }

    return false;
  };

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      if (event === 'SIGNED_IN') {
        markSessionStarted();
      }

      if (event === 'SIGNED_OUT') {
        clearSessionStarted();
      }

      if (session?.user && enforceSessionLimit(session)) {
        setLoading(false);
        return;
      }

      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (session?.user && enforceSessionLimit(session)) {
        setLoading(false);
        return;
      }
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  useEffect(() => {
    const checkExpiry = () => {
      if (!user) return;
      if (isSessionExpired()) {
        expireSession();
      }
    };

    const intervalId = window.setInterval(checkExpiry, SESSION_CHECK_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkExpiry();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      if (!error && data?.session) {
        markSessionStarted();
      }
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        clearSessionStarted();
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.maybeSingle()
      if (!error && data) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const permissions = useMemo(
    () => getEffectivePermissions(userProfile),
    [userProfile]
  );

  const hasPermission = (permission) => checkPermission(userProfile, permission);

  const value = {
    user,
    userProfile,
    permissions,
    hasPermission,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    sessionMaxMinutes: SESSION_MAX_MINUTES,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
